import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { toggleTheme } from '../../store/slices/themeSlice';
import { logout } from '../../store/slices/userSlice';
import { clearAuthData } from '../../utils/localStorage';
import routes from '../../utils/routes';
import { CText } from '../common';
import {
  FiHome,
  FiTrendingUp,
  FiGrid,
  FiPieChart,
  FiUser,
  FiDollarSign,
  FiUsers,
  FiCalendar,
  FiBell,
  FiStar,
  FiSettings,
  FiHelpCircle,
  FiFileText,
  FiShield,
  FiInfo,
  FiLogOut,
  FiMoon,
  FiSun,
  FiChevronDown,
  FiChevronRight,
  FiMessageSquare,
  FiX,
} from 'react-icons/fi';

interface NavSection {
  title: string;
  items: NavItem[];
}

interface NavItem {
  name: string;
  icon: any;
  route?: string;
  action?: string;
  badge?: number;
}

interface SideNavProps {
  isMobile?: boolean;
  onClose?: () => void;
}

const SideNav: React.FC<SideNavProps> = ({ isMobile = false, onClose }) => {
  const colors = useAppSelector((state) => state.theme.theme);
  const isDark = useAppSelector((state) => state.theme.isDark);
  const user = useAppSelector((state) => state.user.user);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [expandedSections, setExpandedSections] = useState<string[]>(['Main', 'Expert', 'Expert Dashboard']);
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  // Build navigation sections dynamically based on user role
  const navSections: NavSection[] = [
    {
      title: 'Main',
      items: [
        { name: 'Home', icon: FiHome, route: routes.home },
        { name: 'Market', icon: FiTrendingUp, route: routes.market },
        { name: 'Buckets', icon: FiGrid, route: routes.buckets },
        { name: 'Copilot', icon: FiMessageSquare, route: routes.copilot },
        { name: 'Portfolio', icon: FiPieChart, route: routes.portfolio },
        { name: 'Wallet', icon: FiDollarSign, route: routes.wallet },
      ],
    },
    {
      title: 'Expert',
      items: [
        { name: 'Find Experts', icon: FiUsers, route: routes.expertSearch },
        { name: 'My Bookings', icon: FiCalendar, route: routes.allBookings, badge: 2 },
      ],
    },
    // Expert Dashboard - only visible for experts
    ...(user?.is_expert ? [{
      title: 'Expert Dashboard',
      items: [
        { name: 'Expert Settings', icon: FiSettings, route: routes.expertSettings },
        { name: 'Availability', icon: FiCalendar, route: routes.expertAvailability },
        { name: 'Session Rates', icon: FiDollarSign, route: routes.expertRates },
        { name: 'Wallet & Payouts', icon: FiDollarSign, route: routes.expertWallet },
      ],
    }] : []),
    {
      title: 'Features',
      items: [
        { name: 'Notifications', icon: FiBell, route: routes.notification, badge: 3 },
        { name: 'Watchlist', icon: FiStar, route: routes.watchListHomeCard },
      ],
    },
    {
      title: 'Settings',
      items: [
        { name: 'Profile', icon: FiUser, route: routes.profile },
        { name: 'Preferences', icon: FiSettings, route: routes.pushNotification },
        { name: 'Dark Mode', icon: isDark ? FiSun : FiMoon, action: 'toggleTheme' },
      ],
    },
    {
      title: 'Support',
      items: [
        { name: 'Help Center', icon: FiHelpCircle, route: routes.helpCenter },
        { name: 'Terms & Conditions', icon: FiFileText, route: routes.termsAndCondition },
        { name: 'Privacy Policy', icon: FiShield, route: routes.privacyPolicies },
        { name: 'About', icon: FiInfo, route: routes.aboutApp },
      ],
    },
  ];

  const isActiveTab = (route?: string) => {
    if (!route) return false;
    return location.pathname === route || location.pathname.startsWith(route + '/');
  };

  const toggleSection = (title: string) => {
    setExpandedSections((prev) =>
      prev.includes(title) ? prev.filter((s) => s !== title) : [...prev, title]
    );
  };

  const handleItemClick = (item: NavItem) => {
    if (item.action === 'toggleTheme') {
      dispatch(toggleTheme());
    } else if (item.route) {
      navigate(item.route);
      if (isMobile && onClose) {
        onClose();
      }
    }
  };

  const handleLogout = () => {
    clearAuthData();
    dispatch(logout());
    navigate(routes.login);
    setShowLogoutModal(false);
  };

  return (
    <>
      <aside
        className={
          isMobile
            ? 'fixed left-0 top-0 h-full w-72 z-[60] flex flex-col border-r overflow-hidden animate-slide-in-left'
            : 'hidden lg:flex flex-col fixed left-0 top-0 h-full w-72 border-r z-50 overflow-hidden'
        }
        style={{
          backgroundColor: colors.glassBackground || colors.backgroundColor,
          borderColor: colors.glassBorder || colors.borderColor,
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          boxShadow: colors.shadowLarge,
        }}
      >
        {/* Close button for mobile */}
        {isMobile && (
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-full hover:opacity-80 transition-opacity z-10"
            style={{ backgroundColor: colors.inputBackground }}
          >
            {React.createElement(FiX as any, { size: 20, color: colors.textColor })}
          </button>
        )}

        {/* Logo/Brand */}
        <div
          className="p-5 border-b flex items-center gap-3"
          style={{ borderColor: colors.borderColor }}
        >
          <img
            src="/logos/logo-tech.svg"
            alt="Yantrai"
            className="w-11 h-11 rounded-xl shadow-lg"
            style={{ boxShadow: colors.shadowGlow }}
          />
          <div>
            <h1 className="text-lg font-bold gradient-text">
              Yantrai
            </h1>
            <p className="text-xs" style={{ color: colors.grayScale500 }}>
              Investment Platform
            </p>
          </div>
        </div>

        {/* User Info */}
        {user && (
          <div
            className="p-4 border-b flex items-center gap-3"
            style={{ borderColor: colors.borderColor }}
          >
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold"
              style={{ backgroundColor: colors.primary, color: colors.white }}
            >
              {user.name?.charAt(0) || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <p
                className="font-medium text-sm truncate"
                style={{ color: colors.textColor }}
              >
                {user.name || 'User'}
              </p>
              <p className="text-xs truncate" style={{ color: colors.grayScale500 }}>
                {user.email || 'user@example.com'}
              </p>
            </div>
          </div>
        )}

        {/* Navigation Sections */}
        <nav className="flex-1 overflow-y-auto py-2">
          {navSections.map((section) => {
            const isExpanded = expandedSections.includes(section.title);

            return (
              <div key={section.title} className="mb-1">
                {/* Section Header */}
                <button
                  onClick={() => toggleSection(section.title)}
                  className="w-full flex items-center justify-between px-4 py-2 text-left"
                >
                  <span
                    className="text-xs font-semibold uppercase tracking-wider"
                    style={{ color: colors.grayScale500 }}
                  >
                    {section.title}
                  </span>
                  {React.createElement(
                    isExpanded ? FiChevronDown : FiChevronRight as any,
                    { size: 14, color: colors.grayScale500 }
                  )}
                </button>

                {/* Section Items */}
                {isExpanded && (
                  <div className="space-y-0.5">
                    {section.items.map((item) => {
                      const isActive = isActiveTab(item.route);
                      const Icon = item.icon;

                      return (
                        <button
                          key={item.name}
                          onClick={() => handleItemClick(item)}
                          className="w-full flex items-center gap-3 px-4 py-2.5 mx-2 text-left transition-all duration-200 relative rounded-xl hover:translate-x-1"
                          style={{
                            color: isActive ? colors.primary : colors.textColor,
                            backgroundColor: isActive ? `${colors.primary}15` : 'transparent',
                            boxShadow: isActive ? colors.shadowSoft : 'none',
                            width: 'calc(100% - 16px)',
                          }}
                          onMouseEnter={(e) => {
                            if (!isActive) {
                              e.currentTarget.style.backgroundColor = colors.hoverBackground || `${colors.primary}08`;
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isActive) {
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }
                          }}
                        >
                          {/* Active indicator */}
                          {isActive && (
                            <div
                              className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 rounded-r-full"
                              style={{ backgroundColor: colors.primary }}
                            />
                          )}

                          {React.createElement(Icon as any, {
                            size: 18,
                            color: isActive ? colors.primary : colors.grayScale500,
                          })}

                          <span
                            className="flex-1 text-sm"
                            style={{
                              fontWeight: isActive ? 600 : 400,
                            }}
                          >
                            {item.name}
                          </span>

                          {/* Badge */}
                          {item.badge && item.badge > 0 && (
                            <span
                              className="px-1.5 py-0.5 rounded-full text-xs font-medium"
                              style={{
                                backgroundColor: colors.alertColor,
                                color: colors.white,
                              }}
                            >
                              {item.badge}
                            </span>
                          )}

                          {/* Theme toggle indicator */}
                          {item.action === 'toggleTheme' && (
                            <div
                              className="w-8 h-4 rounded-full relative transition-colors"
                              style={{
                                backgroundColor: isDark ? colors.primary : colors.grayScale500 + '40',
                              }}
                            >
                              <div
                                className="absolute top-0.5 w-3 h-3 rounded-full transition-all"
                                style={{
                                  backgroundColor: colors.white,
                                  left: isDark ? '18px' : '2px',
                                }}
                              />
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* Logout Button */}
        <div className="p-4 border-t" style={{ borderColor: colors.borderColor }}>
          <button
            onClick={() => setShowLogoutModal(true)}
            className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl transition-colors"
            style={{
              backgroundColor: colors.alertColor + '15',
              color: colors.alertColor,
            }}
          >
            {React.createElement(FiLogOut as any, { size: 18 })}
            <span className="text-sm font-medium">Logout</span>
          </button>
        </div>

        {/* Footer */}
        <div
          className="px-4 py-3 border-t text-center"
          style={{
            borderColor: colors.borderColor,
            color: colors.grayScale500,
          }}
        >
          <p className="text-xs">© 2024 Yantrai v1.0.0</p>
        </div>
      </aside>

      {/* Logout Confirmation Modal */}
      {showLogoutModal && (
        <div
          className="fixed inset-0 flex items-center justify-center z-[100] p-4 animate-fade-in"
          style={{ backgroundColor: colors.modalBackground }}
        >
          <div
            className="rounded-2xl p-6 w-full max-w-sm animate-scale-in"
            style={{
              backgroundColor: colors.cardBackground || colors.backgroundColor,
              boxShadow: colors.shadowLarge,
            }}
          >
            <div className="text-center mb-6">
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                style={{ backgroundColor: colors.alertColor + '20' }}
              >
                {React.createElement(FiLogOut as any, { size: 28, color: colors.alertColor })}
              </div>
              <CText type="B18" color={colors.textColor}>
                Logout?
              </CText>
              <CText type="r14" color={colors.grayScale500} style={{ marginTop: 8 }}>
                Are you sure you want to logout from your account?
              </CText>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowLogoutModal(false)}
                className="flex-1 py-3 rounded-xl border"
                style={{ borderColor: colors.borderColor }}
              >
                <CText type="M14" color={colors.textColor}>
                  Cancel
                </CText>
              </button>
              <button
                onClick={handleLogout}
                className="flex-1 py-3 rounded-xl"
                style={{ backgroundColor: colors.alertColor }}
              >
                <CText type="M14" color={colors.white}>
                  Logout
                </CText>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SideNav;
